import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import debounce from "lodash/debounce";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import InputAdornment from "@material-ui/core/InputAdornment";
import A_TextField from "../../../Atoms/Inputs/TextFields/A_TextField";
import A_Chip from "../../../Atoms/DataDisplay/Chips/A_Chip";
import IconAction from "./IconAction";
import M_DataStateRenderer from "../../../Molecules/Common/DataStateRenderer/M_DataStateRenderer.jsx";
import LoaderSkeleton from "../../../Molecules/Common/LoaderSkeleton";
import StatusTag from "./StatusTag";
import useStyles from "./ProfessionalContacts.styles";
import {
  ALL_FIRMS_TAB,
  FILTER_CHIPS,
  buildFirmQueryParams,
  api,
} from "./ProfessionalContacts.helper";
import { firmTypeLabel } from "../../../../../domain/professionalContacts/professionalContacts.types";

const RepositoryList = ({ tab, onOpenFirm }) => {
  const classes = useStyles();
  const [searchText, setSearchText] = useState("");
  const [activeChips, setActiveChips] = useState(() => new Set());
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resetToken, setResetToken] = useState(0);
  const requestSeq = useRef(0);

  const load = useCallback(async (currentTab, text, chips) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const page = await api.listFirms(
        buildFirmQueryParams(currentTab, text, chips)
      );
      if (seq !== requestSeq.current) return;
      setItems(page?.items || []);
      setTotal(page?.total ?? page?.items?.length ?? 0);
    } catch (err) {
      if (seq === requestSeq.current) setError(err);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  const debouncedLoad = useMemo(() => debounce(load, 350), [load]);

  useEffect(() => () => debouncedLoad.cancel(), [debouncedLoad]);

  useEffect(() => {
    load(tab, searchText, activeChips);
  }, [tab]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchText(value);
    debouncedLoad(tab, value, activeChips);
  };

  const toggleChip = (key) => {
    const next = new Set(activeChips);
    next.has(key) ? next.delete(key) : next.add(key);
    setActiveChips(next);
    load(tab, searchText, next);
  };

  const clearSearch = () => {
    setSearchText("");
    setActiveChips(new Set());
    setResetToken((token) => token + 1);
    load(tab, "", new Set());
  };

  const piiTone = (status) => {
    if (status === "Valid") return "valid";
    if (status === "Expiring") return "expiring";
    return "neutral";
  };

  const isFiltered = Boolean(searchText) || activeChips.size > 0;

  return (
    <div>
      <div className={classes.toolbar}>
        <div className={classes.chipsRow}>
          {FILTER_CHIPS.map(({ key, label }) => (
            <A_Chip
              key={key}
              label={label}
              size="small"
              color={activeChips.has(key) ? "primary" : "default"}
              variant={activeChips.has(key) ? "default" : "outlined"}
              onClick={() => toggleChip(key)}
            />
          ))}
        </div>

        <div className={classes.searchBlock}>
          <div className={classes.toolbarRight}>
            <A_TextField
              key={resetToken}
              id="professional-contacts-search"
              className={classes.searchInput}
              label="Search"
              defaultValue={searchText}
              onChange={handleSearchChange}
              margin="none"
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      style={{
                        fontSize: "1rem",
                        color: "var(--color-text-secondary)",
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            {isFiltered && (
              <IconAction
                title="Clear Search and Filters"
                icon={<ClearIcon fontSize="small" />}
                onClick={clearSearch}
              />
            )}
          </div>
          <span className={classes.resultCount}>
            {loading ? "Searching…" : `${items.length} of ${total}`}
          </span>
        </div>
      </div>

      <div className={classes.hint}>
        <InfoOutlinedIcon className={classes.hintIcon} />
        <span>
          Smart search matches firm name, type, individuals, emails, phone, town
          &amp; postcode. Chips stack with the text search.
        </span>
      </div>

      <div className={clsx(classes.tableContainer, classes.tableSpaced)}>
        <M_DataStateRenderer
          loading={loading && items.length === 0}
          error={error}
          data={items}
          isEmpty={!loading && !error && items.length === 0}
          skeleton={<LoaderSkeleton />}
          empty={
            <div className={classes.emptyState}>
              {isFiltered
                ? "No firms match your search or filters."
                : "No firms yet."}
              {isFiltered && (
                <div className={classes.emptyStateActions}>
                  <button
                    type="button"
                    className={classes.linkAction}
                    onClick={clearSearch}
                  >
                    Clear Search
                  </button>
                </div>
              )}
            </div>
          }
        >
          <Table
            size="small"
            className={clsx(classes.table, classes.tableWide)}
          >
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Firm</TableCell>
                <TableCell>PII</TableCell>
                <TableCell align="center">Contacts</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="center">Instructing</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody className={classes.tableBody}>
              {items.map((firm) => (
                <TableRow
                  key={firm.id}
                  hover
                  className={classes.clickableRow}
                  onClick={() => onOpenFirm(firm.id)}
                >
                  <TableCell>
                    <div className={classes.cellStrong}>{firm.name}</div>
                    <div className={classes.cellMeta}>
                      {firmTypeLabel(firm.type)}{" "}
                      {firm.mainContactName ? `· ${firm.mainContactName}` : ""}
                    </div>
                  </TableCell>
                  <TableCell>
                    {firm.piiStatus && firm.piiStatus !== "None" ? (
                      <StatusTag tone={piiTone(firm.piiStatus)}>
                        {firm.piiStatus}
                      </StatusTag>
                    ) : (
                      <span className={classes.subtle}>-</span>
                    )}
                  </TableCell>
                  <TableCell align="center">{firm.contactCount ?? "-"}</TableCell>
                  <TableCell align="center">
                    {firm.contactCount ?? "-"}
                  </TableCell>
                  <TableCell align="center">
                    <StatusTag tone={firm.active ? "valid" : "neutral"}>
                      {firm.active ? "Active" : "Inactive"}
                    </StatusTag>
                  </TableCell>
                  <TableCell align="center">
                    {firm.noFurtherInstructions ? (
                      <StatusTag tone="expiring">Paused</StatusTag>
                    ) : (
                      <span className={classes.subtle}>
                        {firm.active ? "Yes" : "No"}
                      </span>
                    )}
                  </TableCell>
                  <TableCell align="right" className={classes.cellNowrap}>
                    <IconAction
                      title={`Open ${firm.name}`}
                      icon={<ChevronRightIcon fontSize="small" />}
                      onClick={() => onOpenFirm(firm.id)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </M_DataStateRenderer>
      </div>
    </div>
  );
};

RepositoryList.propTypes = {
  tab: PropTypes.string,
  onOpenFirm: PropTypes.func.isRequired,
};

RepositoryList.defaultProps = {
  tab: ALL_FIRMS_TAB,
};

export default RepositoryList;
